import type { AppliedLimitThreshold } from '@/lib/api/generated/model';

/**
 * Colour for the three threshold states (spec 072 FR-029, FR-030).
 *
 * Every value is an existing semantic token. Nothing here invents a shade: `docs/brand.md`
 * FD-12 forbids extending the palette and OD-10 records that no tint ramp exists to
 * extend it with, so the only tints are `/N` opacity modifiers over those same tokens —
 * which is how the repository already expresses hover, selection and the overview's
 * washes.
 *
 * Under and over reuse the board's own colours rather than a scale of their own. A page
 * that said "over limit" in a different red from the board it links to would undo in one
 * glance the agreement the rest of the product works to keep. Approaching is the warning
 * colour (spec 251), not the board's waiting purple: purple means *needs a person*
 * everywhere else, and a limit coming up is about money, not a person.
 *
 * **Colour never travels alone here.** Every use of these is paired with a label, because
 * the three states are exactly the kind of ordered scale that colour-blind readers cannot
 * rank by hue (FR-030).
 */

/** The filled portion of the spend meter. */
export const THRESHOLD_FILL: Record<AppliedLimitThreshold, string> = {
  under: 'bg-board-state-running',
  approaching: 'bg-state-warning',
  exhausted: 'bg-board-state-failed',
};

/**
 * The forecast's stretch of the meter (spec 253): stripes of the fill's colour, so the
 * part that has not happened yet reads as the same quantity, not yet spent. The same
 * pattern as the board's waiting-on-subtasks fill, on these tokens.
 */
export const THRESHOLD_HATCH: Record<AppliedLimitThreshold, string> = {
  under:
    'bg-[repeating-linear-gradient(135deg,var(--color-board-state-running)_0_2px,transparent_2px_5px)]',
  approaching:
    'bg-[repeating-linear-gradient(135deg,var(--color-state-warning)_0_2px,transparent_2px_5px)]',
  exhausted:
    'bg-[repeating-linear-gradient(135deg,var(--color-board-state-failed)_0_2px,transparent_2px_5px)]',
};

/** The service's approaching cut-off (APPROACHING_RATIO), applied to a forecast. */
export const APPROACHING_RATIO = 0.8;

const RANK: Record<AppliedLimitThreshold, number> = {
  under: 0,
  approaching: 1,
  exhausted: 2,
};

/** Where a ratio of the limit lands on the three states. */
export function thresholdFor(ratio: number): AppliedLimitThreshold {
  if (ratio >= 1) return 'exhausted';
  if (ratio >= APPROACHING_RATIO) return 'approaching';
  return 'under';
}

/** The worse of two states: the meter's fill colour (spec 253). */
export function worseThreshold(
  a: AppliedLimitThreshold,
  b: AppliedLimitThreshold,
): AppliedLimitThreshold {
  return RANK[a] >= RANK[b] ? a : b;
}

/** The threshold's own word, coloured to match its fill. */
export const THRESHOLD_TEXT: Record<AppliedLimitThreshold, string> = {
  under: 'text-board-state-running',
  approaching: 'text-state-warning',
  exhausted: 'text-board-state-failed',
};

/**
 * The box a threshold chip is printed in.
 *
 * The colour below travelled between seven call sites; the shape did not, and
 * each site retyped it. Six settled on this, and one — the workspace limit chip
 * — reached for `rounded-md px-2 py-1` instead, with a comment explaining the
 * imported colour and nothing about the different corners. Two chips saying the
 * same thing in different shapes on adjacent screens is the drift this closes.
 */
export const THRESHOLD_CHIP_BOX =
  'rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap';

/** The chip a threshold word is printed in, matching the overview's chips. */
export const THRESHOLD_CHIP: Record<AppliedLimitThreshold, string> = {
  under: 'bg-board-state-running/12 text-board-state-running',
  approaching: 'bg-state-warning/12 text-state-warning',
  exhausted: 'bg-board-state-failed/12 text-board-state-failed',
};

/**
 * What each state is called, once.
 *
 * The words are the contract's states rendered for a reader, and both the workspace page
 * and the organization console use these — two surfaces naming the same state
 * differently is how a reader learns to distrust both.
 */
export const THRESHOLD_LABEL: Record<AppliedLimitThreshold, string> = {
  under: 'Within limit',
  approaching: 'Approaching limit',
  exhausted: 'Limit reached',
};
